import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, ShieldCheck, Loader2 } from "lucide-react";

const FORM_ENDPOINT = "/api/guest-slot.php";
const ROLES = ["Founder / Entrepreneur", "Creator / Artist", "Leader / Politician", "Guru / Thought Leader", "PR / Representative"];

const inputCls =
  "w-full rounded-2xl bg-white/[0.04] border border-white/10 px-4 sm:px-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none backdrop-blur-xl transition-colors duration-300 focus:border-amber-500/50 focus:bg-white/[0.06]";

const EMPTY = { name: "", email: "", role: ROLES[0], profile: "", pitch: "", _honey: "" };

const SlotBookingForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          role: form.role,
          profile: form.profile,
          pitch: form.pitch,
          _honey: form._honey,
          sourceUrl: window.location.href,
        }),
      });
      const result = await res.json().catch(() => ({}));
      if (!res.ok || result.success === false || result.success === "false") {
        throw new Error(result.message || "Submission failed");
      }
      toast.success("Slot request sent", {
        description: `Thank you, ${form.name.split(" ")[0] || "guest"}. Your nomination has been emailed to the MyIQ Talks team.`,
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your request", {
        description: "Please try again, or email us directly at ankushseminars@gmail.com.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="request-slot" className="relative py-20 sm:py-28 px-4">
      <div className="ambient-glow w-[320px] h-[320px] sm:w-[560px] sm:h-[560px] bg-amber-500/15 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative max-w-4xl mx-auto clay-card p-6 sm:p-12 lg:p-16"
      >
        <div className="text-center max-w-xl mx-auto">
          <p className="text-xs font-semibold tracking-[0.22em] uppercase text-amber-400">Request a Slot</p>
          <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            Your story deserves
            <span className="text-gold-gradient"> this chair.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed flex items-start sm:items-center justify-center gap-2 text-left sm:text-center">
            <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
            Every nomination is personally reviewed by Ankush and the editorial team.
          </p>
        </div>

        <form onSubmit={onSubmit} className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <input
            data-testid="slot-form-name-input"
            required
            value={form.name}
            onChange={set("name")}
            placeholder="Full name"
            autoComplete="name"
            className={inputCls}
          />
          <input
            data-testid="slot-form-email-input"
            required
            type="email"
            value={form.email}
            onChange={set("email")}
            placeholder="Email address"
            autoComplete="email"
            className={inputCls}
          />
          <select
            data-testid="slot-form-category-select"
            value={form.role}
            onChange={set("role")}
            className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-slate-900`}
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <input
            data-testid="slot-form-profile-input"
            type="url"
            value={form.profile}
            onChange={set("profile")}
            placeholder="LinkedIn / website / portfolio link"
            className={inputCls}
          />
          <textarea
            data-testid="slot-form-pitch-textarea"
            required
            value={form.pitch}
            onChange={set("pitch")}
            rows={4}
            placeholder="In two or three lines — what is your money or growth story?"
            className={`${inputCls} sm:col-span-2 resize-none`}
          />
          <div className="absolute -left-[9999px]" aria-hidden="true">
            <input
              data-testid="slot-form-honeypot"
              value={form._honey}
              onChange={set("_honey")}
              tabIndex={-1}
              autoComplete="off"
              placeholder="Leave this field empty"
            />
          </div>
          <div className="sm:col-span-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-6 mt-2">
            <p className="text-xs text-slate-500 leading-relaxed max-w-none sm:max-w-xs text-center sm:text-left">
              Prefer email? Write to <a href="mailto:ankushseminars@gmail.com" className="text-amber-300 hover:text-amber-200 transition-colors">ankushseminars@gmail.com</a> and the team will respond within 48 hours.
            </p>
            <button
              type="submit"
              disabled={sending}
              data-testid="slot-form-submit-button"
              className="tactile-btn inline-flex items-center justify-center gap-2 rounded-full w-full sm:w-auto px-6 sm:px-10 py-4 text-sm font-bold disabled:opacity-60 disabled:pointer-events-none"
            >
              {sending ? (
                <>Sending <Loader2 className="w-4 h-4 animate-spin" /></>
              ) : (
                <>Request a Slot <Send className="w-4 h-4" /></>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </section>
  );
};

export default SlotBookingForm;
